import React from 'react'

import Page from 'src/comps/Page'
import { PropertyProvider } from 'src/context/property'
import { getServerSideProps as getServerSidePropsHandler } from 'src/pages/hotel/[country]/[slug]/server'
import type { ConfigType } from 'src/types/config'
import type { PropertyAPIResponse as PropertyType } from 'src/types/property'
import Content from 'src/pages/hotel/[country]/[slug]/Content'
import type { ListOfAncillary } from '@s4l/stayforlong/booking/ListOfAncillary'
import type { ListOfBanner } from '@s4l/stayforlong/marketing/ListOfBanner'
import useExperiment from 'src/hooks/useExperimentConfig'
import { useEffectOnce } from 'react-use'
import { hasToNotifyRedirectExperiment, REDIRECT_EXPERIMENT } from 'src/constants/experimentConfig'
import { useRouter } from 'next/router'

export const pageId = 'detail'

interface IHotelPageProps {
  config: ConfigType
  property: PropertyType
  location: { country: string; type: string; slug: string }
  ancillariesJSON: ReturnType<ListOfAncillary['toJSON']>
  bannersJSON?: ReturnType<ListOfBanner['toJSON']>
}

function HotelPage({
  property,
  config,
  location,
  ancillariesJSON,
  bannersJSON,
}: Readonly<IHotelPageProps>) {
  const { query } = useRouter()
  const { notify: notifyRedirectExperiment } = useExperiment(
    REDIRECT_EXPERIMENT.ID,
    variant => variant === REDIRECT_EXPERIMENT.REDIRECT_TO_DETAILS
  )

  useEffectOnce(() => {
    const { aid, offer_type: offerType } = query

    if (typeof aid !== 'string') return
    if (typeof offerType !== 'string') return

    if (
      hasToNotifyRedirectExperiment({
        aid,
        offerType,
      })
    ) {
      notifyRedirectExperiment()
    }
  })

  const contentLocation = {
    ...location,
    city: property.city,
    purchaseId: '',
  }

  return (
    <PropertyProvider value={property}>
      <Page
        key={property?.id}
        config={config}
        pageId={pageId}
        trackingData={{ propertyId: property?.id }}
      >
        <Content
          property={property}
          location={contentLocation}
          ancillariesJSON={ancillariesJSON}
          bannersJSON={bannersJSON}
        />
      </Page>
    </PropertyProvider>
  )
}

export const getServerSideProps = getServerSidePropsHandler

HotelPage.pageId = pageId
HotelPage.pagePathView = '/hotel/[country]/[slug]'

export default HotelPage
